import React from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import styled from '@emotion/styled'
import { mq } from '../helpers'
import { HowItWorksWrapper } from '../components/how-it-works/styles'
import Header from '../components/how-it-works/Header'
import Tagline from '../components/how-it-works/Tagline'
import Steps from '../components/how-it-works/Steps'
import BookCTA from '../components/how-it-works/BookCTA'
import Prepping from '../components/how-it-works/Prepping'
import Cancellation from '../components/how-it-works/Cancellation'
import GroupAffair from '../components/how-it-works/GroupAffair'
import WeddingGlam from '../components/how-it-works/WeddingGlam'
import FAQ from '../components/FAQ'
import { getFaqsByTag } from '../services/strapi'

// The gallery sits near the bottom of the page and pulls in Swiper (JS + CSS).
// Defer it so that code stays off the initial load path. The placeholder
// reserves the section's height to avoid layout shift when it mounts.
const GalleryPlaceholder = styled.div`
  width: 100%;
  max-width: 1600px;
  min-height: 760px;

  ${mq('max', '768px')} {
    min-height: 380px;
  }
`

const GalleryCarousel = dynamic(
  () => import('../components/how-it-works/GalleryCarousel'),
  { ssr: false, loading: () => <GalleryPlaceholder aria-hidden /> }
)

const PAGE_TITLE = 'How It Works | GLAMSQUAD'
const PAGE_DESCRIPTION = 'Book in-home beauty services in 4 easy steps. Enter your address, choose your services, pick a time, and a licensed GLAMSQUAD pro comes to you.'
const PAGE_URL = 'https://glamsquad.com/how-it-works'
const PAGE_OG_IMAGE = 'https://glamsquad.com/how-it-works/how_it_works_banner.webp'

const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to book an in-home beauty appointment with GLAMSQUAD',
  description: PAGE_DESCRIPTION,
  totalTime: 'PT5M',
  image: PAGE_OG_IMAGE,
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Select a service', text: 'Choose from blowouts, makeup, full glam, nails, and more. Same-day service available.', url: `${PAGE_URL}#step-1` },
    { '@type': 'HowToStep', position: 2, name: 'Choose date, time, and location', text: 'Pick a time that works for you and tell us where to show up — we come to you, on your schedule.', url: `${PAGE_URL}#step-2` },
    { '@type': 'HowToStep', position: 3, name: 'Match with top-tier beauty pros', text: 'Get paired with a vetted, certified pro who specializes in exactly what you need.', url: `${PAGE_URL}#step-3` },
    { '@type': 'HowToStep', position: 4, name: 'Sit back and get glam', text: 'Your pro handles the rest — you just show up and glow up.', url: `${PAGE_URL}#step-4` },
  ],
}

const HowItWorks = ({ faqs }) => {
  return (
    <>
      <Head>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_OG_IMAGE} />
        <meta property="og:image:alt" content="GLAMSQUAD in-home beauty service" />
        <meta property="og:site_name" content="GLAMSQUAD" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_OG_IMAGE} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(howToJsonLd) }}
        />
      </Head>
      <HowItWorksWrapper>
        <Header />
        <Tagline />
        <Steps />
        <BookCTA />
        <Prepping />
        <Cancellation />
        <GroupAffair />
        <WeddingGlam />
        <GalleryCarousel />
        <FAQ faqs={faqs} />
      </HowItWorksWrapper>
    </>
  )
}

export async function getStaticProps() {
  const faqs = await getFaqsByTag('how-it-works')
  return {
    props: { faqs },
    revalidate: 60,
  }
}

export default HowItWorks
